/* eslint-disable react/style-prop-object */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import styles from "./Roles.module.scss";
import { LoadingIndicator } from "../../components/LoadingIndicator/LoadingIndicator";
import { useProducts } from "../../../hooks/useProducts/useProducts";
import { useRoles } from "../../../hooks/useRoles/useRoles";
import { IRoleFilters, RoleFilters } from "../../components/RoleFilters/RoleFilters";
import { RolesTable } from "../../components/RolesTable/RolesTable";
import { Sidebar } from "../../components/Sidebar/Sidebar";
import { IRoleFormValues, RoleForm } from "../../components/RoleForm/RoleForm";
import { Role } from "../../../domain/Role";
import { useUserContext } from "../../../providers/UserProvider";

import { ROLE_MAPPINGS, ALL_PAGES_SIZE, ATTRIBUTE_ROLES, DEFAULT_DEBOUNCE } from "../../../constants/constants";
import { ConfirmationDialog } from "../../components/ConfirmationDialog/ConfirmationDialog";
import { datadogRum } from "@datadog/browser-rum";
import LimitByRole from "../../components/LimitByRole/LimitByRole";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag/useFeatureFlag";
import { Flags } from "../../../feature-flags/flags";
import { getEntitledProductRoles } from "../../../lib/access-helpers";
import { emitToast } from "../../../lib/toaster-utils";
import { AddRoleButton } from "../../components/AddRoleButton/AddRoleButton";
import { SearchInput } from "../../components/SearchInput/SearchInput";
import { useDebounce } from "../../../hooks/useDebounce/useDebounce";
import { ResetFilterPrompt } from "../../components/ResetFilterPrompt/ResetFilterPrompt";

export const Roles = () => {
    const { organizationId } = useUserContext();
    const { products = [], instances = [], isLoading: productsLoading } = useProducts({ organizationId });
    const [showRoleForm, setShowRoleForm] = useState(false);
    const [roleToBeDeleted, setRoleToBeDeleted] = useState<Role | null>(null);
    const [formRole, setFormRole] = useState<Role | undefined>(undefined);
    const [roleDeletionError, setRoleDeletionError] = useState("");
    const [roleDeletionInProgress, setRoleDeletionInProgress] = useState(false);
    const [filters, setFilters] = useState<IRoleFilters>({ product: null, instance: null });
    const { enabled: filterGroupsPerEntitlements } = useFeatureFlag(Flags.FILTER_GROUPS_AND_ROLES_PER_ENTITLEMENTS);
    const { enabled: disableCustomRoleCreation, variables: disabledProductsRoleCreation } = useFeatureFlag(
        Flags.DISABLE_CUSTOM_ROLE_CREATION
    );

    const productsThatAllowCustomRoles = useMemo(
        () =>
            products.filter((p) => {
                const disabledProduct =
                    disableCustomRoleCreation &&
                    (disabledProductsRoleCreation as any)["disabled_products"].products
                        .map((i: any) => i.id)
                        .includes(p.id);

                return p.allowCustomerDefinedRoles && !disabledProduct;
            }),
        [disableCustomRoleCreation, disabledProductsRoleCreation, products]
    );

    const { roles: allRoles } = useRoles({
        organizationId,
        productId: filters?.product?.id,
        instanceId: filters?.instance?.id,
        size: ALL_PAGES_SIZE
    });

    const {
        debouncedValue: debouncedSearchQuery,
        value: searchQuery,
        setValue: setSearchQuery
    } = useDebounce<string>("", DEFAULT_DEBOUNCE);

    const {
        createRole,
        currentPage,
        deleteRole,
        error,
        isLoading,
        pageSize,
        roles = [],
        setCurrentPage,
        totalCount,
        updateRole
    } = useRoles({
        organizationId,
        searchQuery: debouncedSearchQuery.length > 2 ? debouncedSearchQuery : "",
        productId: filters?.product?.id,
        instanceId: filters?.instance?.id
    });

    const updateFilters = useCallback((roleFilters: IRoleFilters) => {
        setFilters(roleFilters);
    }, []);

    const handleRoleDeletion = () => {
        setRoleDeletionInProgress(true);
        setRoleDeletionError("");
        deleteRole({ roleId: roleToBeDeleted!.id })
            .then(() => {
                emitToast({ message: "Role successfully deleted." });
                setRoleToBeDeleted(null);
            })
            .catch((deletionError) => {
                datadogRum.addError(deletionError);
                const [apiError] = deletionError || [deletionError];
                const { message } = apiError || {};
                setRoleDeletionError(message || apiError);
            })
            .finally(() => setRoleDeletionInProgress(false));
    };

    const goToPage = useCallback(
        ({ page = 1 }: { page: number }) => {
            setCurrentPage(page);
        },
        [setCurrentPage]
    );

    const resetRoleFilters = () => {
        setFilters({ instance: null, product: null });
        setSearchQuery("");
    };

    const openRoleSidebar = () => {
        setShowRoleForm(true);
    };

    const closeRoleForm = () => {
        setShowRoleForm(false);
        setFormRole(undefined);
    };

    const handleSubmit = (roleDetails: IRoleFormValues) => {
        if (formRole) {
            return updateRole({ organizationId: organizationId!, id: formRole.id, ...roleDetails });
        }
        return createRole({ organizationId: organizationId!, ...roleDetails });
    };

    const handleRoleClick = ({ role }: { role: Role }) => {
        setFormRole(role);
    };

    const onSuccess = () => {
        closeRoleForm();
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [filters?.product, filters?.instance, setCurrentPage, searchQuery]);

    if (error) return <div>Error fetching roles.</div>;

    let adjustedAllRoles = allRoles;
    let adjustedRoles = roles;
    if (filterGroupsPerEntitlements) {
        adjustedAllRoles = getEntitledProductRoles({ products, instances, roles: allRoles || [] });
        adjustedRoles = getEntitledProductRoles({ products, instances, roles });
    }

    const roleType = ROLE_MAPPINGS[formRole?.roleType!] || ROLE_MAPPINGS["default"];
    const formTitle = !!formRole ? (formRole.isSystemGeneratedRole ? "System Role" : `${roleType} Role`) : "Add Role";
    const noProductWithCustomRoles = !productsThatAllowCustomRoles?.length;

    return (
        <div className={`${styles["roles-page"]} list-page`}>
            <div className={styles["roles-page__bar"]}>
                <div className={styles["roles-page__left-grouping"]}>
                    <div className={styles["roles-page__search"]}>
                        <SearchInput
                            placeholder="Search roles by name..."
                            type="text"
                            value={searchQuery}
                            onChange={({ value }) => {
                                setSearchQuery(value);
                            }}
                        />
                    </div>
                    <RoleFilters
                        products={products}
                        filters={filters}
                        onChange={updateFilters}
                        disableInstances={!adjustedRoles?.length}
                    />
                </div>
                <LimitByRole action={ATTRIBUTE_ROLES.ROLES.CREATE} mode="hide">
                    <div className={styles["roles-page__add-role"]}>
                        <AddRoleButton
                            onClick={openRoleSidebar}
                            tooltip={
                                noProductWithCustomRoles
                                    ? "Custom roles are not available for your current products."
                                    : undefined
                            }
                            disabled={!productsThatAllowCustomRoles?.length || productsLoading || isLoading}
                        />
                    </div>
                </LimitByRole>
            </div>
            <LimitByRole mode="unauthorized" action={ATTRIBUTE_ROLES.ROLES.READ}>
                <div className={`${styles["roles-page__table"]} list-table flex flex--column`}>
                    {isLoading && <LoadingIndicator height="100%" type="spinner" overlay />}
                    {!adjustedRoles?.length &&
                        filters?.product?.id &&
                        debouncedSearchQuery.length <= 2 &&
                        !productsThatAllowCustomRoles?.find((p) => p.id === filters?.product?.id) && (
                            <div>
                                Roles are not currently managed for the selected product in the Admin Center. Please
                                navigate to the product to manage roles.
                            </div>
                        )}
                    {(debouncedSearchQuery.length > 2 || filters.product || filters.instance) &&
                        !adjustedRoles?.length &&
                        !isLoading && (
                            <div className="push--top">
                                <ResetFilterPrompt onClick={resetRoleFilters} prompt="No roles match your filter." />
                            </div>
                        )}

                    <RolesTable
                        roles={adjustedRoles}
                        instances={instances}
                        onDeleteClick={({ role }) => setRoleToBeDeleted(role)}
                        onRoleClick={handleRoleClick}
                        onPagination={goToPage}
                        page={currentPage}
                        total={totalCount}
                        pageSize={pageSize}
                        products={products}
                    />
                </div>
            </LimitByRole>
            {!!roleToBeDeleted && (
                <ConfirmationDialog
                    callToActionText="Delete"
                    error={roleDeletionError}
                    loading={roleDeletionInProgress}
                    onCancel={() => setRoleToBeDeleted(null)}
                    onConfirmation={handleRoleDeletion}
                    title="Delete Role"
                >
                    You are about to delete this role.
                    {roleToBeDeleted.productId === process.env.REACT_APP_CMP_PRODUCT_ID &&
                    roleToBeDeleted?.attributes?.some((attribute) => attribute.key === "Team")
                        ? " Deleting a custom role with the Teams capability will remove all associated users from the team, revoking their access. "
                        : " It will remove all user access to the instances assigned to this role. "}
                    Are you sure you want to continue?
                </ConfirmationDialog>
            )}
            {(showRoleForm || formRole) && (
                <Sidebar heading={formTitle} isOpen onCancel={closeRoleForm} noTransform>
                    <RoleForm
                        allRoles={adjustedAllRoles}
                        onSuccess={onSuccess}
                        role={formRole}
                        products={products}
                        onClose={closeRoleForm}
                        onSubmit={handleSubmit}
                    />
                </Sidebar>
            )}
        </div>
    );
};

Roles.displayName = "RolesPage";
